/**
 * Charger anomalies.
 *
 * The page opens on what is wrong now, not on a search box. Most days nothing
 * is open and the honest answer is a sentence saying so -- an empty table is
 * ambiguous, because it looks the same whether the fleet is healthy or the
 * detector is broken.
 *
 * The thing this screen has to get across is that severity is not decoration.
 * Only a critical hardware hazard is ever allowed to stop a customer charging.
 * Supply problems -- voltage high or low -- are reported and never actioned,
 * because the charger already protects itself and pulling the plug for a grid
 * condition would turn the safety net into the outage. That rule is written on
 * the page rather than left in the code.
 */
import React, { useCallback, useEffect, useState } from "react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import "./anomalies.scss";
import { useNotice } from "../shared/useNotice";
import {
  dt, ago, lasted, inr, kwh, wh, count, measured,
  severityStory, stateStory, actionStory, kindStory, sourceStory, faultStory, Tone,
} from "./story";
import {
  anomaliesApi, Anomaly, AnomalySummary, ErrorCodePolicy, MeterFault, Severity,
} from "../../../api/maintenance/anomalies";

type Tab = "open" | "meters" | "ranking";

const KINDS = [
  "over_power", "over_current", "over_voltage", "under_voltage",
  "over_temperature", "temperature_rise", "frequency_deviation",
  "low_power_factor", "phase_imbalance", "charger_error_code",
];

/* ------------------------------------------------------------------ pieces */

const Badge: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`an-badge ${tone}`}>{children}</span>
);

const Empty: React.FC<{ icon: string; title: string; body: React.ReactNode }> = ({
  icon, title, body,
}) => (
  <div className="an-empty">
    <div className="an-empty__icon">{icon}</div>
    <h3>{title}</h3>
    <p>{body}</p>
  </div>
);

const Figures: React.FC<{
  rows: Array<[label: string, value: React.ReactNode, note?: string, cls?: string]>;
}> = ({ rows }) => (
  <div className="an-figures">
    {rows.map(([label, value, note, cls]) => (
      <div key={label}>
        <span>{label}</span>
        <b className={cls}>{value}</b>
        {note ? <em>{note}</em> : null}
      </div>
    ))}
  </div>
);

/* ----------------------------------------------------------------- console */

const AnomalyConsole: React.FC = () => {
  const canEdit = canEditMaintenance();
  const [tab, setTab] = useState<Tab>("open");
  const [days, setDays] = useState(7);
  const [err, setErr] = useNotice("error", null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [summary, setSummary] = useState<AnomalySummary | null>(null);
  const [rows, setRows] = useState<Anomaly[]>([]);
  const [faults, setFaults] = useState<MeterFault[]>([]);
  const [ranking, setRanking] = useState<ErrorCodePolicy[]>([]);
  const [saving, setSaving] = useState("");

  const [severity, setSeverity] = useState<Severity | "">("");
  const [kind, setKind] = useState("");
  const [device, setDevice] = useState("");
  const [openOnly, setOpenOnly] = useState(true);

  const fail = (e: any, what: string) =>
    setErr(e?.response?.data?.detail || `Could not load ${what}`);

  const loadSummary = useCallback(async () => {
    try { setSummary((await anomaliesApi.summary(days)).data); }
    catch (e) { fail(e, "the summary"); }
  }, [days]);

  const loadOpen = useCallback(async () => {
    setBusy(true);
    try {
      const r = await anomaliesApi.list({ severity, kind, device, open_only: openOnly, days, page_size: 100 });
      setRows(r.data.items); setErr(null);
    } catch (e) { fail(e, "anomalies"); } finally { setBusy(false); }
  }, [severity, kind, device, openOnly, days]);

  const loadFaults = useCallback(async () => {
    setBusy(true);
    try { setFaults((await anomaliesApi.meterFaults(Math.max(days, 30), device)).data.items); }
    catch (e) { fail(e, "broken meters"); } finally { setBusy(false); }
  }, [days, device]);

  const loadRanking = useCallback(async () => {
    try { setRanking((await anomaliesApi.errorCodePolicy()).data); }
    catch (e) { fail(e, "the error code ranking"); }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => {
    if (tab === "open") loadOpen();
    else if (tab === "meters") loadFaults();
    else loadRanking();
  }, [tab, loadOpen, loadFaults, loadRanking]);

  const retune = async (p: ErrorCodePolicy, severityNext: Severity, actionableNext: boolean) => {
    setSaving(p.error_code); setErr(null); setFlash(null);
    try {
      await anomaliesApi.updateErrorCodePolicy(p.error_code, {
        severity: severityNext, actionable: actionableNext, description: p.description,
      });
      setFlash(`${p.error_code} is now ${severityNext}. Chargers pick this up within about five minutes — nothing to deploy.`);
      await loadRanking();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || `Could not update ${p.error_code}`);
    } finally { setSaving(""); }
  };

  const a = summary?.anomalies;
  const mf = summary?.meter_faults;

  // An empty table looks the same whether the fleet is fine or the detector is
  // broken, so say which.
  const headline = () => {
    if (!a) return "Reading the last few days of charger behaviour…";
    if (a.open_critical > 0)
      return `${count(a.open_critical)} critical ${a.open_critical === 1 ? "anomaly is" : "anomalies are"} open right now. These are the only ones that can stop a customer charging.`;
    if (a.open_total > 0)
      return `${count(a.open_total)} open across ${count(a.devices_affected)} charger${a.devices_affected === 1 ? "" : "s"}, none critical. Reported, but nothing has been stopped or reset.`;
    return `Nothing open. ${count(a.total_in_window)} ${a.total_in_window === 1 ? "anomaly" : "anomalies"} came and went in the last ${days} day${days === 1 ? "" : "s"}.`;
  };

  return (
    <MaintenanceLayout>
      <div className="an-page">
        <header className="an-head">
          <div className="an-headrow">
            <div>
              <h1>Charger anomalies</h1>
              <p>{headline()}</p>
            </div>
            <div className="an-window">
              {[1, 7, 30].map((d) => (
                <button key={d} className={days === d ? "on" : ""} onClick={() => setDays(d)}>
                  {d}d
                </button>
              ))}
            </div>
          </div>
        </header>

        {err && <div className="an-err" onClick={() => setErr(null)}>{err}</div>}
        {flash && <div className="an-flash" onClick={() => setFlash(null)}>{flash}</div>}

        <div className="an-card">
          <Figures
            rows={[
              ["Open now", count(a?.open_total), `${count(a?.devices_affected)} chargers`],
              ["Critical", count(a?.open_critical), "only these stop a session",
                a && a.open_critical > 0 ? "bad" : undefined],
              ["Warnings", count(a?.open_warning), "reported, never actioned",
                a && a.open_warning > 0 ? "warn" : undefined],
              ["Sessions stopped", count(a?.sessions_stopped),
                `${count(a?.chargers_reset)} reset · ${count(a?.resets_deferred)} deferred`],
              ["Unbilled energy", kwh(mf?.unbilled_kwh),
                `${inr(mf?.unbilled_value)} over ${count(mf?.faults)} sessions`],
            ]}
          />
        </div>

        <nav className="an-tabs">
          <button className={tab === "open" ? "on" : ""} onClick={() => setTab("open")}>
            Anomalies
            {a && a.open_total > 0 ? <span className="an-tabs__count">{count(a.open_total)}</span> : null}
          </button>
          <button className={tab === "meters" ? "on" : ""} onClick={() => setTab("meters")}>
            Broken meters
            {mf && mf.faults > 0 ? <span className="an-tabs__count">{count(mf.faults)}</span> : null}
          </button>
          <button className={tab === "ranking" ? "on" : ""} onClick={() => setTab("ranking")}>
            Error code ranking
          </button>
        </nav>

        {tab === "open" && (
          <div className="an-card">
            <div className="an-filters">
              <select value={severity} onChange={(e) => setSeverity(e.target.value as Severity | "")}>
                <option value="">Every severity</option>
                <option value="critical">Critical only</option>
                <option value="warning">Warning only</option>
                <option value="info">Info only</option>
              </select>
              <select value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="">Every kind</option>
                {KINDS.map((k) => <option key={k} value={k}>{kindStory(k).label}</option>)}
              </select>
              <input placeholder="Charger id" value={device} onChange={(e) => setDevice(e.target.value)} />
              <label>
                <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
                Still open
              </label>
              <button onClick={loadOpen} disabled={busy}>Apply</button>
            </div>

            {busy && <div className="an-loading">Loading…</div>}

            {!busy && rows.length === 0 && (
              <Empty
                icon="✓"
                title={openOnly ? "Nothing open" : "Nothing in this window"}
                body={
                  <>
                    Chargers are watched continuously — every MeterValues reading and every fault
                    the charger raises itself. Nothing here means nothing crossed a threshold
                    {openOnly ? ", or everything that did has since recovered" : ""}. Widen the
                    window or untick <b>Still open</b> to see what has already cleared.
                  </>
                }
              />
            )}

            {!busy && rows.length > 0 && (
              <div className="an-tablewrap">
                <table className="an-table">
                  <thead>
                    <tr>
                      <th>Charger</th><th>What happened</th><th>Severity</th><th>State</th>
                      <th className="num">Measured</th><th className="num">Allowed</th>
                      <th className="num">Readings</th><th className="num">Lasted</th>
                      <th>Acted</th><th>Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const sev = severityStory(r.severity);
                      const st = stateStory(r.state);
                      const k = kindStory(r.kind);
                      const stop = actionStory(r.stop_state);
                      const reset = actionStory(r.reset_state);
                      return (
                        <tr key={r.id}>
                          <td className="who">
                            {r.device_ocpp_id}{r.connector_number ? ` · c${r.connector_number}` : ""}
                            <span className="sub">{r.station_name || "station not recorded"}</span>
                          </td>
                          <td>
                            {k.label}
                            <span className="sub">{r.detail || r.charger_error_code || k.means}</span>
                          </td>
                          <td><Badge tone={sev.tone}>{sev.label}</Badge></td>
                          <td><Badge tone={st.tone}>{st.label}</Badge></td>
                          <td className="num">{measured(r.observed_value, r.unit)}</td>
                          <td className="num">
                            {measured(r.limit_value, r.unit)}
                            {r.limit_source ? <span className="sub">{r.limit_source.replace(/_/g, " ")}</span> : null}
                          </td>
                          <td className="num">{count(r.breach_count)}</td>
                          <td className="num">{lasted(r.duration_seconds)}</td>
                          <td>
                            {stop ? <Badge tone={stop.tone}>stop {stop.label}</Badge> : null}
                            {reset ? <Badge tone={reset.tone}>reset {reset.label}</Badge> : null}
                            {!stop && !reset ? <Badge tone="muted">nothing</Badge> : null}
                          </td>
                          <td>{ago(r.last_seen_at)}<span className="sub">{dt(r.last_seen_at)}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "meters" && (
          <div className="an-card">
            <h3>Sessions the charger could not meter</h3>
            <p className="an-muted">
              When a charger's energy register is unusable, the delivered energy is rebuilt from
              whatever else it reports. <b>Exact</b> means real meter data reassembled from the
              charger's own forward increments. <b>Estimated</b> means integrated from power or
              current, and is the figure to check first if a customer disputes a bill.
            </p>

            {busy && <div className="an-loading">Loading…</div>}

            {!busy && faults.length === 0 && (
              <Empty
                icon="✓"
                title="Every meter behaved"
                body="No session in this window had an unusable energy register. When one does it appears here with what it should have been billed."
              />
            )}

            {!busy && faults.length > 0 && (
              <div className="an-tablewrap">
                <table className="an-table">
                  <thead>
                    <tr>
                      <th>Charger</th><th>What the meter did</th><th>Recovered</th>
                      <th className="num">Billable</th><th className="num">Delivered</th>
                      <th className="num">Unbilled</th><th className="num">Value</th>
                      <th>Charged?</th><th>Reset</th><th>Detected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {faults.map((f) => {
                      const src = sourceStory(f.energy_source);
                      const reset = actionStory(f.restart_state);
                      return (
                        <tr key={f.id}>
                          <td className="who">
                            {f.device_ocpp_id}{f.connector_number ? ` · c${f.connector_number}` : ""}
                            <span className="sub">{f.station_name || "station not recorded"}</span>
                          </td>
                          <td>
                            {f.fault_kind.replace(/_/g, " ")}
                            <span className="sub">{faultStory(f.fault_kind)}</span>
                          </td>
                          <td>
                            <Badge tone={src.tone}>{src.label}</Badge>
                            <span className="sub">{src.means}</span>
                          </td>
                          <td className="num">{wh(f.billed_wh)}</td>
                          <td className="num">{wh(f.recovered_wh)}</td>
                          <td className="num">{wh(f.unbilled_wh)}</td>
                          <td className="num">{inr(f.unbilled_value)}</td>
                          <td>
                            <Badge tone={f.billing_applied ? "ok" : "muted"}>
                              {f.billing_applied ? "billed" : "written off"}
                            </Badge>
                          </td>
                          <td>{reset ? <Badge tone={reset.tone}>{reset.label}</Badge> : <Badge tone="muted">none</Badge>}</td>
                          <td>{ago(f.detected_at)}<span className="sub">{dt(f.detected_at)}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "ranking" && (
          <div className="an-card an-rank">
            <h3>How seriously to take each charger fault</h3>
            <p className="an-muted">
              Chargers raise these codes themselves. What each one is worth is a judgement about
              your fleet, not a property of the protocol — <b>OtherError</b> is a genuine hazard on
              one vendor and a link blip on another. Changes here reach every charger within about
              five minutes; nothing is deployed and nothing restarts.
              <br />
              <b>May stop a session</b> can only ever narrow. A session is stopped only when the
              code is <i>also</i> ranked critical, so ticking it on a lower rank arms nothing.
            </p>

            {ranking.length === 0 && (
              <Empty
                icon="⚙"
                title="Ranking not configured yet"
                body={
                  <>
                    Chargers are using the built-in defaults. Apply
                    {" "}<code>2026-09-05_charger_error_code_policy.sql</code>{" "}
                    to make them editable here.
                  </>
                }
              />
            )}

            {ranking.length > 0 && (
              <div className="an-tablewrap">
                <table className="an-table">
                  <thead>
                    <tr>
                      <th>Code</th><th>Rank</th><th>May stop a session</th>
                      <th>What it means</th><th>Changed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranking.map((p) => {
                      const sev = severityStory(p.severity);
                      const disabled = !canEdit || saving === p.error_code;
                      return (
                        <tr key={p.error_code}>
                          <td className="who">{p.error_code}</td>
                          <td>
                            <select
                              value={p.severity}
                              disabled={disabled}
                              onChange={(e) => retune(p, e.target.value as Severity, p.actionable)}
                            >
                              <option value="info">Info — record only</option>
                              <option value="warning">Warning — tell someone</option>
                              <option value="critical">Critical — may stop a session</option>
                            </select>
                            <span className="sub">{sev.means}</span>
                          </td>
                          <td>
                            <label>
                              <input
                                type="checkbox"
                                checked={p.actionable}
                                disabled={disabled}
                                onChange={(e) => retune(p, p.severity, e.target.checked)}
                              />
                              {p.actionable && p.severity !== "critical"
                                ? <span className="an-rank__note">inactive — needs critical</span>
                                : null}
                            </label>
                          </td>
                          <td>{p.description || "—"}</td>
                          <td>{p.updated_at ? ago(p.updated_at) : "never"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {!canEdit && <p className="an-muted">Read-only — changing the ranking needs super-admin.</p>}
          </div>
        )}

        {summary && summary.by_device.length > 0 && (
          <div className="an-card">
            <h3>Chargers worth looking at</h3>
            <div className="an-tablewrap">
              <table className="an-table">
                <thead>
                  <tr>
                    <th>Charger</th><th>Station</th><th className="num">Anomalies</th>
                    <th className="num">Critical</th><th className="num">Warning</th>
                    <th className="num">Open</th><th className="num">Readings</th>
                    <th>Kinds</th><th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.by_device.map((d) => (
                    <tr key={d.device_ocpp_id}>
                      <td className="who">{d.device_ocpp_id}</td>
                      <td>{d.station_name || "—"}</td>
                      <td className="num">{count(d.anomalies)}</td>
                      <td className="num">{d.critical > 0 ? <Badge tone="bad">{count(d.critical)}</Badge> : "—"}</td>
                      <td className="num">{d.warning > 0 ? <Badge tone="warn">{count(d.warning)}</Badge> : "—"}</td>
                      <td className="num">{d.open_now > 0 ? <Badge tone="warn">{count(d.open_now)}</Badge> : "—"}</td>
                      <td className="num">{count(d.total_breaches)}</td>
                      <td>{d.kinds.split(", ").map((k) => kindStory(k).label).join(", ")}</td>
                      <td>{ago(d.last_seen_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </MaintenanceLayout>
  );
};

export default AnomalyConsole;
